<template>
  <div class="debt-installment-bills">
    <!-- Loader -->
    <Loader
      v-if="loaderEnabled"
      v-bind:logo="loaderLogo"
    />

    <!-- Main Container -->
    <b-row>
      <b-col
        md="12"
        lg="12"
        sm="12"
      >
        <KTCodePreview v-bind:title="' '">
          <!-- Toolbar with Actions -->
          <template v-slot:toolbar>
            <div class="row">
              <div>
                <b-dropdown
                  size="sm"
                  id="dropdown-1"
                  right
                >
                  <template slot="button-content">
                    <i
                      style="font-size: 1rem"
                      class="fas fa-cog"
                    ></i>
                    <span class="font-weight-bolder">Thao tác</span>
                  </template>

                  <!-- Export Excel -->
                  <b-dropdown-item @click="showExcelModal">
                    <span>
                      <i
                        style="font-size: 1rem"
                        class="far fa-file-excel"
                      ></i>
                      &nbsp; Xuất Excel
                    </span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
          </template>

          <template v-slot:preview>
            <!-- Filter Component -->
            <DebtInstallmentBillFilter
              v-model="filters"
              :installment-options="availableInstallments"
              :store-options="availableStores"
              :is-loading="onLoadingList"
              @apply-filters="onApplyFilters"
            />

            <!-- Table Component -->
            <DebtInstallmentBillTable
              :data="mainList.dataset"
              :is-loading="onLoadingList"
              :totals="calculatedTotals"
              @edit-payment="onEditPayment"
              @cash-payment="onCashPayment"
            />

            <!-- Pagination Component -->
            <DebtInstallmentBillPagination
              :total="mainList.total"
              :total-pages="mainList.pages"
              :current-page="apiParams.page"
              :page-size="apiParams.pageSize"
              :filter-params="getFilterParams()"
              @page-change="onPageChange"
            />
          </template>
        </KTCodePreview>
      </b-col>
    </b-row>

    <!-- Modals -->
    <DebtEditModal
      :item="selectedBill"
      :mode="modalPaymentType"
      v-on:refresh="fetchMainData"
    />

    <DebtExcel
      :param="apiParams"
      :mode="modalPaymentType"
    />
  </div>
</template>

<script>
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { getAddDate, getSubtractDate } from '@/utils/date';
import { getToastInstance } from '@/utils/toastHelper';
import KTCodePreview from '@/view/content/CodePreview.vue';
import Loader from '@/view/content/Loader.vue';
import { mapGetters } from 'vuex';

// Import refactored components
import DebtInstallmentBillFilter from './components/DebtInstallmentBillFilter.vue';
import DebtInstallmentBillPagination from './components/DebtInstallmentBillPagination.vue';
import DebtInstallmentBillTable from './components/DebtInstallmentBillTable.vue';

// Import shared components
import DebtEditModal from '@/view/pages/debt/shared/components/DebtEditModal.vue';
import DebtExcel from '@/view/pages/debt/shared/components/DebtExcel.vue';

// Import service layer
import _ from 'lodash';
import DebtInstallmentBillService from './services/DebtInstallmentBillService.js';

const MIN_RANGE_DATE = getSubtractDate(7);
const MAX_RANGE_DATE = getAddDate();

export default {
  name: 'DebtInstallmentBillList',
  components: {
    KTCodePreview,
    Loader,
    DebtInstallmentBillFilter,
    DebtInstallmentBillTable,
    DebtInstallmentBillPagination,
    DebtEditModal,
    DebtExcel,
  },
  data() {
    return {
      title: 'Công nợ trả góp hóa đơn theo ngày',

      // Main data
      mainList: {
        dataset: [],
        total: 0,
        pages: 1,
      },

      // Filter state
      filters: {
        fromDate: MIN_RANGE_DATE,
        toDate: MAX_RANGE_DATE,
        storeId: null,
        contactId: null,
        contractCode: null,
        docId: null,
        customerMobile: null,
        customerName: null,
        isFullPaid: null,
      },

      // API parameters
      apiParams: {
        page: 1,
        pageSize: 10,
        fromDate: MIN_RANGE_DATE,
        toDate: MAX_RANGE_DATE,
        storeId: null,
        contactId: null,
        contractCode: null,
        docId: null,
        customerMobile: null,
        customerName: null,
        isFullPaid: null,
      },

      // UI state
      onLoadingList: false,
      availableInstallments: [],
      availableStores: [],

      // Modal state
      selectedBill: { billNumber: null },
      modalPaymentType: null,
    };
  },
  computed: {
    ...mapGetters(['breadcrumbs', 'pageTitle', 'layoutConfig']),

    /**
     * Check if loader should be enabled
     */
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'));
    },

    /**
     * Get loader logo path
     */
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo');
    },

    /**
     * Calculate summary totals for table footer
     */
    calculatedTotals() {
      return DebtInstallmentBillService.calculateSummaryTotals(
        this.mainList.dataset,
      );
    },
  },
  async created() {
    try {
      // Load breadcrumb
      this.loadBreadcrumb();

      // Get filters from URL
      this.getFiltersFromUrl();

      // Load initial data concurrently
      await this.loadInitialData();

      // Fetch main data
      await this.fetchMainData();
    } catch (error) {
      console.error('Error in component creation:', error);
      this.makeToastFailure('Có lỗi xảy ra khi tải dữ liệu');
    }
  },
  methods: {
    ...getToastInstance(),

    /**
     * Load initial data (installments and stores)
     */
    async loadInitialData() {
      try {
        const { installments, stores } =
          await DebtInstallmentBillService.loadInitialData();
        this.availableInstallments = installments;
        this.availableStores = stores;

        // Initialize store selection if only one store
        if (stores.length === 2) {
          // "All stores" + 1 actual store
          const actualStore = stores.find((store) => store.id !== null);
          if (actualStore) {
            this.filters.storeId = actualStore.id;
            this.apiParams.storeId = actualStore.id;
          }
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        this.makeToastFailure('Không thể tải dữ liệu khởi tạo');
      }
    },

    /**
     * Fetch main transaction data
     */
    async fetchMainData() {
      try {
        this.onLoadingList = true;

        // Build API parameters
        const params = DebtInstallmentBillService.buildApiParameters(
          this.filters,
          this.$route,
        );
        this.apiParams = { ...this.apiParams, ...params };

        // Fetch data
        const response =
          await DebtInstallmentBillService.getInstallmentBillTransactions(
            this.apiParams,
          );
        this.mainList = response;
      } catch (error) {
        console.error('Error fetching main data:', error);
        const message = error.response?.data?.message || 'Lỗi khi tải dữ liệu';
        this.makeToastFailure(message);
      } finally {
        this.onLoadingList = false;
      }
    },

    /**
     * Handle filter application
     */
    async onApplyFilters(newFilters) {
      try {
        // Validate filters
        const validation =
          DebtInstallmentBillService.validateFilters(newFilters);
        if (!validation.isValid) {
          validation.errors.forEach((error) => this.makeToastFailure(error));
          return;
        }

        // Update filters and reset to first page
        this.filters = { ...newFilters };
        this.apiParams.page = 1;

        // Update URL
        this.updateUrl();

        // Fetch new data
        await this.fetchMainData();
      } catch (error) {
        console.error('Error applying filters:', error);
        this.makeToastFailure('Có lỗi xảy ra khi áp dụng bộ lọc');
      }
    },

    /**
     * Handle page change
     */
    async onPageChange(page) {
      try {
        this.apiParams.page = page;
        await this.fetchMainData();
      } catch (error) {
        console.error('Error changing page:', error);
        this.makeToastFailure('Có lỗi xảy ra khi chuyển trang');
      }
    },

    /**
     * Handle edit payment action
     */
    onEditPayment(bill) {
      this.modalPaymentType = 'items';
      this.selectedBill.billNumber = bill.billNumber;
      this.$bvModal.show('md-debt');
    },

    /**
     * Handle cash payment action
     */
    onCashPayment({ bill, item }) {
      const note = DebtInstallmentBillService.generatePaymentNote(bill, item);

      this.$router.push({
        name: 'transaction',
        params: {
          mainModelProp: {
            contactType: '3',
            reconciliationDocType: '1',
            reconciliationDocId: bill.billNumber,
            type: '1',
            contactId: item.paymentRefId,
            contactName: item.paymentName,
            amount: item.paymentAmount,
            note,
          },
          mode: 'cash',
          action: 'add',
          id: 'new',
        },
      });
    },

    /**
     * Show Excel export modal
     */
    showExcelModal() {
      this.modalPaymentType = 'items';
      this.$bvModal.show('modal-debt-excel');
    },

    /**
     * Load breadcrumb navigation
     */
    loadBreadcrumb() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Kế toán', route: 'accountant' },
        { title: 'Công nợ' },
        { title: this.title },
      ]);
    },

    /**
     * Get filters from URL query parameters
     */
    getFiltersFromUrl() {
      const query = this.$route.query || {};

      if (Object.keys(query).length > 0) {
        this.filters = {
          ...this.filters,
          storeId: query.storeId || null,
          fromDate: query.fromDate || MIN_RANGE_DATE,
          toDate: query.toDate || MAX_RANGE_DATE,
          contactId: query.contactId || null,
          contractCode: query.contractCode || null,
          docId: query.docId || null,
          customerMobile: query.customerMobile || null,
          customerName: query.customerName || null,
          isFullPaid: query.isFullPaid || null,
        };

        this.apiParams.page = parseInt(query.page) || 1;
      }
    },

    /**
     * Update URL with current filter parameters
     */
    updateUrl() {
      const params = this.getFilterParams();

      this.$router.push({
        name: 'list-debt-installment-bill',
        query: _.omitBy(params, _.isNil),
      });
    },

    /**
     * Get filter parameters for URL
     */
    getFilterParams() {
      return {
        storeId: this.filters.storeId,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        contactId: this.filters.contactId,
        contractCode: this.filters.contractCode,
        docId: this.filters.docId,
        customerMobile: this.filters.customerMobile,
        customerName: this.filters.customerName,
        isFullPaid: this.filters.isFullPaid,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-installment-bills {
  // Component-specific styles
  .icon:hover {
    background-color: #90c6fc;
  }
}
</style>
